import React, { useRef, useEffect, useState } from 'react';
import { AgentComment, useComments } from '../../../contexts/CommentsContext';
import { useAgent } from '../../../contexts/AgentContext';
import {
  CommentsProps,
  CommentBubbleProps,
  Direction,
  calculateTailDirection,
  calculateTooltipPosition,
  generateRandomPosition,
  getCommentPosition,
} from './comment-helpers';
import './comments.scss';

const CommentBubble: React.FC<CommentBubbleProps> = ({
  comment,
  position,
  baseElementRef,
}) => {
  const [isAnimating, setIsAnimating] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<Direction>('top');
  const [tailDirection, setTailDirection] = useState<Direction>('bottom');
  const bubbleRef = useRef<HTMLDivElement>(null);
  const { getAgentById } = useAgent();
  const { deleteComment } = useComments();

  // Update tail direction when position changes
  useEffect(() => {
    const direction = calculateTailDirection(bubbleRef, baseElementRef);
    setTailDirection(direction);
  }, [position, baseElementRef]);

  // Update tooltip position when hovered
  useEffect(() => {
    if (isHovered) {
      const tooltipPos = calculateTooltipPosition(bubbleRef);
      setTooltipPosition(tooltipPos);
    }
  }, [isHovered, bubbleRef]);

  // Animation effect for new comments
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 600); // Animation duration
    return () => clearTimeout(timer);
  }, []);

  const creatorName =
    comment.createdByAgentName || comment.createdByUserName || 'Unknown';
  const isAgent = !!comment.createdByAgentId;
  const agent =
    isAgent && comment.createdByAgentId
      ? getAgentById(comment.createdByAgentId)
      : null;

  const handleDeleteComment = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await deleteComment(comment.id);
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  return (
    <div
      ref={bubbleRef}
      className={`comment-bubble ${
        isAgent ? 'comment-bubble--agent' : 'comment-bubble--user'
      } ${isAnimating ? 'comment-bubble--animating' : ''}`}
      style={{
        left: position.x,
        top: position.y,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Collapsed avatar bubble */}
      <div
        className={`comment-bubble__collapsed comment-bubble__collapsed--tail-${tailDirection}`}
      >
        {agent?.image_url ? (
          <img
            src={agent.image_url}
            alt={creatorName}
            className='comment-bubble__avatar'
          />
        ) : (
          <div className='comment-bubble__avatar comment-bubble__avatar--fallback'>
            {isAgent ? 'A' : 'U'}
          </div>
        )}
      </div>

      {/* Expanded tooltip on hover */}
      {isHovered && (
        <div
          className={`comment-bubble__tooltip comment-bubble__tooltip--${tooltipPosition}`}
        >
          <div
            className={`comment-bubble__expanded ${
              isHovered ? 'comment-bubble__expanded--visible' : ''
            }`}
          >
            {/* Delete button */}
            <button
              className='comment-bubble__delete'
              onClick={handleDeleteComment}
              title='Delete comment'
              aria-label='Delete comment'
            >
              ×
            </button>

            {/* Arrow */}
            <div
              className={`comment-bubble__arrow comment-bubble__arrow--${tooltipPosition}`}
            />
            {/* Comment header */}
            <div className='comment-bubble__header'>
              <div className='comment-bubble__creator'>
                {agent?.image_url ? (
                  <img
                    src={agent.image_url}
                    alt={creatorName}
                    className='comment-bubble__avatar'
                  />
                ) : (
                  <div className='comment-bubble__avatar comment-bubble__avatar--fallback'>
                    {isAgent ? 'A' : 'U'}
                  </div>
                )}
                <span className='comment-bubble__name'>{creatorName}</span>
              </div>
              <span className='comment-bubble__time'>
                {new Date(comment.createdAt).toLocaleTimeString()}
              </span>
            </div>

            {/* Comment content */}
            <div className='comment-bubble__content'>{comment.content}</div>

            {/* Status indicator */}
            {comment.status === 'resolved' && (
              <div className='comment-bubble__status'>Resolved</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const Comments: React.FC<CommentsProps> = ({
  comments,
  baseElementRef,
}) => {
  const { updateCommentPosition } = useComments();
  const [containerBounds, setContainerBounds] = useState<DOMRect | null>(null);
  const [commentPositions, setCommentPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});

  // Calculate container bounds when baseElementRef changes
  useEffect(() => {
    if (baseElementRef?.current) {
      const bounds = baseElementRef.current.getBoundingClientRect();
      setContainerBounds(bounds);
    }
  }, [baseElementRef]);

  // Generate random positions for comments without saved positions
  useEffect(() => {
    if (!containerBounds) return;

    const newPositions: Record<string, { x: number; y: number }> = {};
    let hasNewPositions = false;

    comments.forEach((comment) => {
      if (!comment.position?.x || !comment.position?.y) {
        const randomPosition = generateRandomPosition(containerBounds);
        newPositions[comment.id] = randomPosition;
        hasNewPositions = true;

        // Update the backend with the new position
        updateCommentPosition(comment.id, randomPosition).catch((error) => {
          console.error('Failed to save random comment position:', error);
        });
      }
    });

    if (hasNewPositions) {
      setCommentPositions((prev) => ({ ...prev, ...newPositions }));
    }
  }, [comments, containerBounds, updateCommentPosition]);

  if (!comments || comments.length === 0) {
    return null;
  }

  return (
    <div className='absolute inset-0 pointer-events-none'>
      {comments
        .filter((comment) => comment.status === 'active')
        .map((comment) => (
          <div key={comment.id} className='pointer-events-auto'>
            <CommentBubble
              comment={comment}
              position={getCommentPosition(comment, commentPositions)}
              baseElementRef={baseElementRef}
            />
          </div>
        ))}
    </div>
  );
};
